// [FLUID-1 2026-10-12, user] The "fluid" open / close recipe for everything that opens after a click. Compositor-only
// (transform + opacity); every timing lives in motion.ts `fluid`. Browsers without linear(): the spring transition
// line is dropped and the plain emphasized line before it stays. prefers-reduced-motion: no transition, no cascade
// (GlobalStyles zeroes every duration as well).
//   fluidPop     — dropdown lists, menus, calendars, popovers: grows out of its button (scale .92 → 1), 0.34 s / 0.18 s.
//   fluidCascade — the rows / blocks inside rise in one after another (first 10, 16 ms apart, all landed by ~0.45 s).
//                  Fill mode backwards, so a landed row keeps its own hover / selection styles (the ViewSlide lesson).
//   [FLUID-2/3/4 2026-10-12]
//   fluidSheet   — side panels (Bin, Notifications, App Alerts): slide in on the spring (0.34 s), out quicker (0.22 s).
//   fluidScrim   — the shade / blur behind a panel or window: fades in, fades out quicker; no clicks while it goes.
//   fluidDialog  — windows (dialogs / popups): rise 12 px and grow from .95 on the spring, fade in fast, close in 0.18 s.
//   useLandWindow — true for a short window after something opens / data lands, so arriving rows rise in then (and
//                  rows that come later, or a card's own exit animation, are not touched).
//   [FLUID-5 2026-10-12] landRows / useRowsLand — EVERY visible table row rises in, top to bottom, as one wave when rows
//                  land (tab visit, skeleton → data, view switch, date pick); rows out of sight stay still.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { css, keyframes } from 'styled-components';
import { easing, fluid, reducedMotion } from '../styles/motion';
import { getAppZoom } from '../utils/appZoom'; // [APP-FIT 2026-10-26]

export interface FluidPopOptions {
  open: boolean;
  /** transform-origin: the corner the panel grows from — where its button is. Default top left. */
  origin?: string;
  /** A transform the panel needs at rest anyway (translateX(-50%) for a centred popover). */
  base?: string;
  /** The closed state's transform, written after `base`. Default: tucked up 8px, scale .92. */
  from?: string;
  /** Extra transition entries, e.g. a delayed visibility flip. */
  extra?: string;
}

const join = (list: string, extra?: string) => (extra ? `${list}, ${extra}` : list);
const popIn = (extra?: string) => join(`transform ${fluid.in} ${easing.emphasized}, opacity ${fluid.fade} ${easing.entrance}`, extra);
const popInSpring = (extra?: string) => join(`transform ${fluid.in} ${fluid.spring}, opacity ${fluid.fade} ${easing.entrance}`, extra);
const popOut = (extra?: string) => join(`transform ${fluid.out} ${easing.exit}, opacity ${fluid.out} ${easing.standard}`, extra);

export const fluidPop = ({ open, origin = '0 0', base = '', from = 'translateY(-8px) scale(0.92)', extra }: FluidPopOptions) => css`
  transform-origin: ${origin};
  transform: ${open ? (base || 'none') : `${base} ${from}`.trim()};
  opacity: ${open ? 1 : 0};
  pointer-events: ${open ? 'auto' : 'none'};
  transition: ${open ? popIn(extra) : popOut(extra)};
  ${open ? `transition: ${popInSpring(extra)};` : ''}
  ${reducedMotion} {
    transition: none;
  }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;
// [FLUID-2 2026-10-12] Side panels: their cards glide in from the side the panel came from.
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to { opacity: 1; transform: none; }
`;
// [FLUID-7 2026-10-12, user] "when I click on dropdowns it shows a little delay": a dropdown's options rose from nothing,
// so the list read as still arriving. Soft: each option starts 35 % visible and 4 px low — readable at once, still a ripple.
const softIn = keyframes`
  from { opacity: 0.35; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;
// [FLUID-8 2026-10-12, user] "the fastness … the same to the complete application": side-panel cards the same way — they
// start 35 % visible, 10 px to the side the panel came from, and glide home (readable at once, still a ripple).
const softSlideIn = keyframes`
  from { opacity: 0.35; transform: translateX(10px); }
  to { opacity: 1; transform: none; }
`;
// [FLUID-3 2026-10-12] For blocks that carry a transform of their own at rest: fade only, never move.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
// [ADDPAY-SCROLL 2026-10-12, user] The soft start without the move — for a cascade's LAST item (see fluidCascade).
const softFadeIn = keyframes`
  from { opacity: 0.35; }
  to { opacity: 1; }
`;

export type CascadeKind = 'rise' | 'slide' | 'fade' | 'soft' | 'softSlide';

/** Rows matching `selector` (relative to the panel, e.g. `[role="option"]` or `> *`) rise in one after another.
 *  Apply it only while the panel is open / landing, so each open replays it and a close never runs it.
 *  `kind`: 'soft' (default — starts 35 % visible, 4 px low), 'softSlide' (35 %, 10 px from the right — side panels),
 *  'rise' (from nothing, 6 px up), 'slide' (from nothing, 18 px from the right), 'fade' (opacity only).
 *  [FLUID-8 2026-10-12, user] 'soft' from the first frame (0 ms, 12 ms apart) is the default: everything that arrives after
 *  a click is readable at once and still ripples in — what FLUID-7 did for the dropdowns, now the whole app. */
export const fluidCascade = (selector: string, startMs: number = fluid.riseStartMs, stepMs: number = fluid.riseStepMs,
  items: number = fluid.riseItems, kind: CascadeKind = 'soft') => css`
  & ${selector} {
    animation: ${kind === 'slide' ? slideIn : kind === 'fade' ? fadeIn : kind === 'soft' ? softIn : kind === 'softSlide' ? softSlideIn : riseIn} ${fluid.rise} ${easing.emphasized} backwards;
    animation-delay: ${startMs + items * stepMs}ms;
  }
  ${Array.from({ length: items }, (_, i) => `
  & ${selector}:nth-child(${i + 1}) { animation-delay: ${startMs + i * stepMs}ms; }`).join('')}
  /* [ADDPAY-SCROLL 2026-10-12, user] "a popup opens but it has a vertical scroll in the start and then removes": the
     LAST item only fades, it never moves. Moved, it hung a few px below its box while it rose, and a box that scrolls
     (the Add Payment window, a dropdown list, a side panel) showed a scrollbar until it landed - the same rule as the
     table wave's last row. Its delay is unchanged (the nth-child rules above). */
  & ${selector}:last-child {
    animation-name: ${kind === 'soft' || kind === 'softSlide' ? softFadeIn : fadeIn};
  }
  ${reducedMotion} {
    & ${selector}, & ${selector}:last-child { animation: none; }
  }
`;

/* [FLUID-5 2026-10-12, user] "the animation should show for the complete table from top to bottom" and, for the Stats /
   Payout Details switch, "it should be for the complete page, not for the upper half only". Every table row a person can
   SEE when rows land (a tab visit, data replacing its skeleton, a view switch, a date pick) rises in, top to bottom, as
   ONE wave that takes about the same time whatever the count: rows sit further apart when there are few and closer
   when there are many, so 40 rows on screen land as fast as 12. Rows out of sight (below the fold, in a hidden pane) are
   left alone, so a 400-row table still only moves what is on screen. It used to be the first 12 rows (data landing),
   14 (view switch) or 10 (date pick), with the rest snapping in or moving as one block.
   The rows get data-fl-land + --fl-land-delay / --fl-land-rise; the rule that animates them is global
   (styles/GlobalStyles.ts). Two alternating keyframes (token ending a / b) make a wave that starts again on the same
   rows restart cleanly instead of carrying on from the last one. */
// [FLUID-6 2026-10-12] 200 (was 260): the last row lands by ~0.5 s on a landing (40 + 200 + 260), the user's "not slow"
// budget; a switch: 90 + 200 + 340 = 0.63 s.
const LAND_SPREAD_MS = 200;   // the first visible row starts → the last one starts
const LAND_STEP_MAX_MS = 18;  // a few rows: no further apart than before
const LAND_STEP_MIN_MS = 4;   // many rows: never so close that the wave reads as a jump
// [FLUID-8 2026-10-12, user] "i dont want the hold" + "the fastness … the same to the complete application": with no ~1 s
// skeleton in front of it, a wave that rose rows from NOTHING (the bottom one ~0.24 s after its data was there) became
// the thing holding the data back. Rows now start 35 % visible from the first frame — readable at once, still the
// top-to-bottom wave over the whole table (0 + 200 + 260 = all in place by ~0.46 s).
const LAND_FROM_OPACITY = 0.35;
let landSeq = 0;

const prefersReducedMotion = (): boolean => {
  try { return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

/** Starts the wave on the visible rows under `root` (every `tbody tr`). Returns the clean-up (also run on its own once
 *  the wave has landed). `startMs` = before the first row moves; `riseMs` = each row's rise. */
export interface LandStyle {
  /** Rows start this visible (0 = from nothing). Default 0.35 (FLUID-8); a switch uses 0.4. */
  fromOpacity?: number;
  /** First row → last row start. Default LAND_SPREAD_MS. */
  spreadMs?: number;
}

/** [FLUID-6 2026-10-12, user] "it feels laggy when I try to change it": a view switch that waves its rows (Dialing PM / Sales,
 *  My Sales, Sales Scoreboard, PM Performance — via ViewSlide) used to rise them from nothing over ~0.63 s, so the view slid
 *  in looking empty and filled in late. That wave is lighter now: rows start 40 % visible as the slide begins and are all in
 *  place (0 + 150 + 260 = 410 ms) as it ends. (EOM / Dashboard, Refunds / Disputes and Stats / Payout Details do not wave
 *  on a switch at all — the user: "the first time someone comes on the tab it shows the animation for the data, and then in
 *  the toggle switch change it will not show an animation".) */
export const SWITCH_WAVE: LandStyle = { fromOpacity: 0.4, spreadMs: 150 };

export function landRows(root: HTMLElement | null, startMs: number = 0, riseMs: number = fluid.riseMs, style: LandStyle = {}): () => void {
  if (!root || !root.isConnected || prefersReducedMotion() || getComputedStyle(root).visibility === 'hidden') return () => {};
  // Where rows can be seen, top to bottom: the window, cut by every ancestor that clips (a scroll box hides the rows
  // below its edge). Sideways is ignored on purpose: a pane sliding in from the side is still on its way in.
  let top = 0;
  let bottom = window.innerHeight;
  for (let el: HTMLElement | null = root; el && el !== document.documentElement; el = el.parentElement) {
    if (getComputedStyle(el).overflowY !== 'visible') {
      const r = el.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
  }
  const seen: { tr: HTMLElement; y: number; x: number; mark: string }[] = [];
  // [FLUID-6 2026-10-12] A little past the fold too: a block above the table can settle a moment after this measure (a
  // KPI strip scaling to fit, a filter row filling in) and lift the table, and the rows that then slide into view must
  // rise with the rest, not sit there at rest (the user's first complaint). Rows that stay below the fold rise unseen.
  const reach = bottom + Math.max(200, (bottom - top) * 0.25);
  // [FLUID-6 2026-10-12] Table by table, each read only down to that reach (its rows go down the page in order), so a
  // long table costs about as many reads as rows on screen. Side-by-side tables are each read from their own top.
  let reads = 0;
  for (const body of Array.from(root.querySelectorAll<HTMLElement>('tbody'))) {
    for (let tr = body.firstElementChild as HTMLElement | null; tr && reads < 1500; tr = tr.nextElementSibling as HTMLElement | null) {
      if (tr.tagName !== 'TR') continue;
      reads += 1;
      const r = tr.getBoundingClientRect();
      // [FLUID-6 2026-10-12] Where the row will REST, not where it is drawn this instant: a re-sort / filter glide
      // (utils/useFlipReorder) draws a moved row at its OLD place first and glides it home, so measuring the drawn box
      // left rows that were sliding up into view out of the wave (they then sat there at rest).
      const tf = getComputedStyle(tr).transform;
      const dy = tf && tf !== 'none' ? new DOMMatrixReadOnly(tf).m42 * getAppZoom() : 0; // [APP-FIT 2026-10-26] page → screen px, like r
      const rTop = r.top - dy;
      const rBottom = r.bottom - dy;
      if (r.height > 0 && rTop >= reach) break;
      if (r.height > 0 && rBottom > top) seen.push({ tr, y: rTop, x: r.left, mark: '' });
    }
  }
  if (!seen.length) return () => {};
  seen.sort((a, b) => a.y - b.y || a.x - b.x); // top to bottom (side-by-side tables share the wave)
  const n = seen.length;
  const step = n > 1 ? Math.max(LAND_STEP_MIN_MS, Math.min(LAND_STEP_MAX_MS, (style.spreadMs ?? LAND_SPREAD_MS) / (n - 1))) : 0;
  const fromOpacity = style.fromOpacity ?? LAND_FROM_OPACITY;
  landSeq += 1;
  seen.forEach((row, i) => {
    const { tr } = row;
    // [FLUID-6 2026-10-12] The keyframe flips PER ROW (from the row's own last mark): a row still carrying an earlier
    // wave's mark restarts cleanly instead of carrying on from it (a global flip matched it again after two waves).
    const before = tr.getAttribute('data-fl-land');
    row.mark = `${landSeq}${before && before.endsWith('a') ? 'b' : 'a'}`;
    tr.style.setProperty('--fl-land-delay', `${startMs + Math.round(i * step)}ms`);
    tr.style.setProperty('--fl-land-rise', `${riseMs}ms`);
    if (fromOpacity > 0) tr.style.setProperty('--fl-land-o', String(fromOpacity));
    else tr.style.removeProperty('--fl-land-o');
    // A table's very LAST row only fades (no 6 px rise): risen, it hung below the table for a moment and the box around
    // the table showed a vertical scrollbar until it landed (user 10-12: "the table shows the vertical scroller … once
    // animation ends … the vertical scroller removes"). Rows above it rise over the row below, inside the table.
    const body = tr.parentElement;
    if (body && tr === body.lastElementChild && !body.nextElementSibling) tr.style.setProperty('--fl-land-y', '0px');
    tr.setAttribute('data-fl-land', row.mark);
  });
  // Let go once the last row has landed (fill mode backwards: nothing visible changes). A newer wave on the same rows
  // owns them, so only this wave's own token is removed.
  const clear = () => {
    for (const { tr, mark } of seen) {
      if (tr.getAttribute('data-fl-land') !== mark) continue;
      tr.removeAttribute('data-fl-land');
      tr.style.removeProperty('--fl-land-delay');
      tr.style.removeProperty('--fl-land-rise');
      tr.style.removeProperty('--fl-land-y');
      tr.style.removeProperty('--fl-land-o');
    }
  };
  const t = window.setTimeout(clear, startMs + Math.round((n - 1) * step) + riseMs + 120);
  return () => { window.clearTimeout(t); clear(); };
}

/** Runs `landRows` on `ref`'s element (before paint, so no row ever shows at rest first) each time `landKey` changes to
 *  a value that is not null / undefined / false. */
export function useRowsLand(ref: RefObject<HTMLElement | null>, landKey: unknown, startMs: number = 0, riseMs: number = fluid.riseMs): void {
  useLayoutEffect(() => {
    if (landKey == null || landKey === false) return undefined;
    return landRows(ref.current, startMs, riseMs);
  }, [ref, landKey, startMs, riseMs]);
}

/** [FLUID-2 2026-10-12] A side panel: slides in on the spring (0.34 s), out quicker (0.22 s). Open = translateX(0)
 *  (NOT none — the panels relied on being a containing block while open). */
export const fluidSheet = (open: boolean, from = 'translateX(100%)') => css`
  transform: ${open ? 'translateX(0)' : from};
  transition: transform ${open ? fluid.sheetIn : fluid.sheetOut} ${open ? easing.emphasized : easing.exit},
    box-shadow ${fluid.sheetIn} ${easing.standard};
  ${open ? `transition: transform ${fluid.sheetIn} ${fluid.spring}, box-shadow ${fluid.sheetIn} ${easing.standard};` : ''}
  ${reducedMotion} {
    transition: none;
  }
`;

/** [FLUID-2/3 2026-10-12] The shade (and any blur) behind a panel or window: fades in 0.22 s, out 0.18 s, and stops
 *  catching clicks the moment it starts to close. The fade-in starts FAST (entrance curve): in most windows the shade
 *  is the box's parent, so its opacity multiplies the box's — a slow-start curve here held the window unreadable. */
export const fluidScrim = (open: boolean) => css`
  opacity: ${open ? 1 : 0};
  pointer-events: ${open ? 'auto' : 'none'};
  transition: opacity ${open ? fluid.scrimIn : fluid.scrimOut} ${open ? easing.entrance : easing.standard};
  ${reducedMotion} {
    transition: none;
  }
`;

const dlgIn = `transform ${fluid.dialogIn} ${easing.emphasized}, opacity ${fluid.fade} ${easing.entrance}`;
const dlgInSpring = `transform ${fluid.dialogIn} ${fluid.spring}, opacity ${fluid.fade} ${easing.entrance}`;
const dlgOut = `transform ${fluid.dialogOut} ${easing.exit}, opacity ${fluid.dialogOut} ${easing.standard}`;

/** [FLUID-3 2026-10-12] A window (dialog / popup box): closed = 12 px lower, scale .95, transparent; open = at rest.
 *  Opens on the spring (0.36 s, readable after ~0.1 s), closes in 0.18 s. Pair with usePresence — mount with
 *  `open && pres.entered` — so the open has a from-state and the close plays before the unmount. */
export const fluidDialog = ({ open, base = '', origin = '50% 50%' }: { open: boolean; base?: string; origin?: string }) => css`
  transform-origin: ${origin};
  transform: ${open ? (base || 'none') : `${base} translateY(12px) scale(0.95)`.trim()};
  opacity: ${open ? 1 : 0};
  pointer-events: ${open ? 'auto' : 'none'};
  transition: ${open ? dlgIn : dlgOut};
  ${open ? `transition: ${dlgInSpring};` : ''}
  ${reducedMotion} {
    transition: none;
  }
`;

/** [FLUID-2/3/4 2026-10-12] True for `ms` after `key` changes while `active` (e.g. a panel opening, or its data
 *  landing), so a cascade runs exactly then and is switched off afterwards — rows that arrive later appear normally
 *  and a card's own animation (a swipe-out) is not overridden. Decided DURING render, not in an effect: an effect
 *  would paint the rows once at full strength and then snap them to the cascade's first frame (a flash). Idempotent,
 *  so a StrictMode double render decides the same way (the ViewSlide pattern). */
export function useLandWindow(active: boolean, key: string, ms = 800): boolean {
  const sig = active ? key : null;
  const prev = useRef<string | null>(null);
  const on = useRef(false);
  const [, settle] = useState(0);
  if (prev.current !== sig) {
    on.current = sig !== null;
    prev.current = sig;
  }
  useEffect(() => {
    if (!on.current) return undefined;
    const t = window.setTimeout(() => { on.current = false; settle((n) => n + 1); }, ms);
    return () => window.clearTimeout(t);
  }, [sig, ms]);
  return on.current;
}
